import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useField, useFormikContext } from 'formik'
import { useInfiniteQuery, useQuery } from 'react-query'

import { useClient } from '@hooks/useClient'
import { useDebounce } from '@hooks/useDebounce'
import { WriterOptionType } from '@models/user/WriterOptionType'
import { useOutside } from '@hooks/useOutside'
import { WithSearchWriterFieldProps, SearchWriterFieldProps } from './interface'

const withSearchWriterField = (Component: React.FC<SearchWriterFieldProps>) => {
  function WithSearchWriterField({
    name,
    resetHandler,
    ...props
  }: WithSearchWriterFieldProps) {
    const client = useClient()
    const searchRef = useRef<HTMLDivElement>(null)
    const [isShowSearchLog, setIsShowSearchLog] = useState<boolean>(false)
    const [textSearch, setTextSearch] = useState('')
    const [text, setText] = useState('')
    const { submitForm } = useFormikContext()
    const [field, , { setValue }] = useField(name)
    const { debounce } = useDebounce()

    const { fetchNextPage, data, isFetchingNextPage } = useInfiniteQuery(
      ['search-writer', 'page', textSearch],
      ({ pageParam = 1 }) =>
        client?.userClient.getWriterOption(textSearch, pageParam),
      {
        getNextPageParam: lastPage => {
          if (lastPage && lastPage.data.length) return lastPage.page + 1

          return undefined
        },
      }
    )

    function handleFetchNextPage() {
      if (!isFetchingNextPage) fetchNextPage()
    }

    function showSearchLog() {
      setIsShowSearchLog(true)
    }

    function hideSearchLog() {
      setIsShowSearchLog(false)
    }

    function handleTextChange(event: React.ChangeEvent<HTMLInputElement>) {
      setText(event.target.value)
    }

    function handleSelectWriter(writer: WriterOptionType) {
      setValue(writer)
      hideSearchLog()
      submitForm()
    }

    function removeTextSearch() {
      if (resetHandler) {
        resetHandler()
      }
      setValue(undefined)
      setText('')
    }

    useOutside(searchRef, () => {
      const prevText = field.value?.user?.username || ''
      hideSearchLog()
      setText(prevText)
    })

    const writers = useMemo(
      () => data?.pages.flatMap(page => page!.data) || [],
      [data]
    )

    useEffect(() => {
      debounce(() => {
        setTextSearch(text)
      }, 400)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text])

    useEffect(() => {
      setText(field.value?.user?.username || '')
    }, [field.value])

    const componentsProps = {
      ...props,
      text,
      isShowSearchLog,
      value: field.value || '',
      writers,
      showSearchLog,
      hideSearchLog,
      handleSelectWriter,
      handleTextChange,
      searchRef,
      handleFetchNextPage,
      removeTextSearch,
    }
    return <Component {...componentsProps} />
  }

  return WithSearchWriterField
}

export { withSearchWriterField }
